import { Bolt, Close, Home, PersonAddAltOutlined } from "@mui/icons-material";
import { Box, Dialog, DialogContent, DialogTitle, Divider, FormHelperText, Grid2, IconButton, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import EntityFormFields from "components/forms/entities/EntityFormFields";
import IndieBackButton from "components/forms/IndieBackButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieIBANTextField from "components/forms/IndieIBANTextField";
import IndieNumberField from "components/forms/IndieNumberField";
import IndieSaveButton from "components/forms/IndieSaveButton";
import IndieTextField from "components/forms/IndieTextField";
import IndiePaymentMethodsSelect from "components/forms/tariffs/IndiePaymentMethodsSelect";
import IndieReceiptMethodsSelect from "components/forms/tariffs/IndieReceiptMethodsSelect";
import ValueListWithMultiSelectCard from "components/forms/ValueListWithMultiSelectCard";
import { CommunityContext } from "contexts/communities/CommunityContext";
import { TariffsContextProvider } from "contexts/tariffs/TariffsContext";
import { useUserFeedbackContext } from "contexts/UserFeedbackContext";
import { Form, Formik, useFormikContext } from "formik";
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectYesNo from "components/SelectYesNo";
import { validateFiscalNumber, validateIBAN } from "utils/utils";
import * as Yup from "yup";
import { IndieLabelAndValue } from "../communities/NewCommunityDialog";

// EGAC is deliberately excluded here — this mini-flow only ever creates Consumer/Producer
// clients for a community.
const CLIENT_TYPES = [
    { code: "consumer", name: "label.tariffPlanType.consumer", icon: <Home /> },
    { code: "producer", name: "label.tariffPlanType.producer", icon: <Bolt /> },
];

const CreateClientDialog = ({ open, onClose, onSuccess, community }) => {

    const { t } = useTranslation();
    const { createClient } = useContext(CommunityContext);
    const { setAndShowErrorMessage } = useUserFeedbackContext();
    const [submitting, setSubmitting] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    useEffect(() => {
        setActiveStep(0);
    }, [open]);

    const initialValues = {
        client_type: [],
        fiscal_number: "",
        name: "",
        phone: "",
        email: "",
        zip_code: "",
        iban: "",
        receipt_tax_regime: "",
        receipt_has_open_activity: false,
        receipt_use_auto_invoicing: "",
        producer_receiving_method: 1,
        consumer_payment_method: "",
        consumer_payment_method_object: {},
        consumer_dd_iban: "",
        consumer_dd_mandate_reference: "",
        consumer_dd_max_value: "",
    };

    const step1ValidationSchema = Yup.object().shape({
        client_type: Yup.array()
            .of(Yup.string().required())
            .min(1, t("error.forms.mandatory"))
            .required(t("error.forms.mandatory")),
    });

    const step2ValidationSchema = Yup.object().shape({
        fiscal_number: Yup.string()
            .required(t("error.forms.mandatory"))
            .matches(/^\d{9}$/, t('error.forms.fiscalNumberDigits'))
            .test('valid-nif', t('error.forms.fiscalNumberInvalid'), (value) => validateFiscalNumber(value)),
        zip_code: Yup.string().required(t("error.forms.mandatory")),
        name: Yup.string().required(t("error.forms.mandatory")),
        email: Yup.string().email(t("error.forms.emailFormat")).required(t("error.forms.mandatory")),
        phone: Yup.string().required(t("error.forms.mandatory")),
        iban: Yup.string().when("client_type", {
            is: (types) => types?.includes("producer"),
            then: (schema) => schema.required(t("error.forms.mandatory")).test('valid-iban', t('error.forms.invalidIBAN'), (value) => !!value && validateIBAN(value)),
            otherwise: (schema) => schema.notRequired().test('valid-iban', t('error.forms.invalidIBAN'), (value) => !value || validateIBAN(value)),
        }),
        receipt_tax_regime: Yup.string().when("client_type", {
            is: (types) => types?.includes("producer"),
            then: (schema) => schema.required(t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
        receipt_use_auto_invoicing: Yup.string().when("client_type", {
            is: (types) => types?.includes("producer"),
            then: (schema) => schema.required(t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
        receipt_has_open_activity: Yup.boolean().when("client_type", {
            is: (types) => types?.includes("producer"),
            then: (schema) => schema.oneOf([true], t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
        consumer_payment_method: Yup.string().when("client_type", {
            is: (types) => types?.includes("consumer"),
            then: (schema) => schema.required(t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
        consumer_dd_iban: Yup.string().when("consumer_payment_method_object", {
            is: (obj) => obj?.code === "sdd",
            then: (schema) => schema.required(t("error.forms.mandatory"))
                .test("valid-iban", t("error.forms.invalidIBAN"), (value) => !!value && validateIBAN(value)),
            otherwise: (schema) => schema.notRequired()
                .test("valid-iban", t("error.forms.invalidIBAN"), (value) => !value || validateIBAN(value)),
        }),
        consumer_dd_mandate_reference: Yup.string().when("consumer_payment_method_object", {
            is: (obj) => obj?.code === "sdd",
            then: (schema) => schema.required(t("error.forms.mandatory")),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const step3ValidationSchema = Yup.object().shape({});

    const validationSchemas = [step1ValidationSchema, step2ValidationSchema, step3ValidationSchema];

    const handleSubmit = (values) => {
        if (activeStep < 2) {
            handleNext();
            return;
        }

        setSubmitting(true);
        createClient(values).then((response) => {
            setSubmitting(false);
            onSuccess(response.data);
        }).catch((error) => {
            setSubmitting(false);
            setAndShowErrorMessage(error);
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack spacing={2} direction="row" alignItems="center">
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                    <PersonAddAltOutlined color="primary" />
                    <Typography variant="subtitle1" color="textPrimary">{t("label.button.createClient")}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <TariffsContextProvider>
                    <Divider fullWidth sx={{ mb: 2 }} />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchemas[activeStep]}
                        validateOnBlur={false}
                        validateOnChange={false}
                        onSubmit={handleSubmit}
                    >
                        {(formik) => (
                            <Form>
                                <Stepper activeStep={activeStep} orientation="vertical">
                                    <Step key={0}>
                                        <StepLabel>{t("addClient.clientType")}</StepLabel>
                                        <StepContent>
                                            <Stack spacing={2}>
                                                <Typography variant="subtitle2">{t("addClient.clientType.subtitle")}</Typography>
                                                <Grid2 container spacing={2}>
                                                    {CLIENT_TYPES.map((type) => (
                                                        <Grid2 item size={{ xs: 12, sm: 6 }} key={type.code}>
                                                            <ValueListWithMultiSelectCard
                                                                id={`card_client_type_${type.code}`}
                                                                name="client_type"
                                                                code={type.code}
                                                                title={t(type.name)}
                                                                icon={type.icon}
                                                                multiple
                                                            />
                                                        </Grid2>
                                                    ))}
                                                </Grid2>
                                                {formik.errors.client_type && <FormHelperText id="client_type_helper_text" error>{formik.errors.client_type}</FormHelperText>}
                                                <Stack direction="row" spacing={2}>
                                                    <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                                </Stack>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                    <Step key={1}>
                                        <StepLabel>{t("label.title.clientData")}</StepLabel>
                                        <StepContent>
                                            <Box maxWidth="lg">
                                                <Stack spacing={3}>
                                                    <EntityFormFields
                                                        showReceiptFormFields
                                                        manual
                                                    />
                                                    <Grid2 container spacing={2}>
                                                        <Grid2 item size={{ xs: 12, sm: 6, lg: 4 }}>
                                                            <Typography variant="subtitle2">{t("label.title.receivingInformation")}</Typography>
                                                            <Stack spacing={1}>
                                                                <IndieReceiptMethodsSelect
                                                                    id="producer_receiving_method"
                                                                    name="producer_receiving_method"
                                                                    label={t("label.form.receiptMethod")}
                                                                    disabled
                                                                />
                                                                <IndieIBANTextField
                                                                    id="iban"
                                                                    name="iban"
                                                                    label={t("label.form.paymentIBAN")}
                                                                    fullWidth
                                                                />
                                                                <SelectYesNo
                                                                    id="receipt_use_auto_invoicing"
                                                                    name="receipt_use_auto_invoicing"
                                                                    label={t("label.form.isAutoInvoicingAgreement")}
                                                                />
                                                            </Stack>
                                                        </Grid2>
                                                        <Grid2 item size={{ xs: 12, sm: 6, lg: 8 }}>
                                                            <Typography variant="subtitle2">{t("label.title.paymentsInformation")}</Typography>
                                                            <Stack spacing={1}>
                                                                <IndiePaymentMethodsSelect
                                                                    id="consumer_payment_method"
                                                                    name="consumer_payment_method"
                                                                    label={t("label.form.paymentMethod")}
                                                                    agent={community?.agent}
                                                                />
                                                                {formik.values.consumer_payment_method_object?.code === "sdd" && (
                                                                    <>
                                                                        <IndieTextField
                                                                            id="consumer_dd_iban"
                                                                            name="consumer_dd_iban"
                                                                            label={t("label.form.ddIBAN")}
                                                                            fullWidth
                                                                        />
                                                                        <IndieTextField
                                                                            id="consumer_dd_mandate_reference"
                                                                            name="consumer_dd_mandate_reference"
                                                                            label={t("label.form.ddMandateReference")}
                                                                            fullWidth
                                                                        />
                                                                        <IndieNumberField
                                                                            id="consumer_dd_max_value"
                                                                            name="consumer_dd_max_value"
                                                                            label={t("label.form.ddMaxValue")}
                                                                            sx={{ width: 250 }}
                                                                        />
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </Grid2>
                                                    </Grid2>
                                                    <Stack direction="row" spacing={2}>
                                                        <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} />
                                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                    <Step key={2}>
                                        <StepLabel>{t("label.step.newCommunity.confirmationStep")}</StepLabel>
                                        <StepContent>
                                            <Stack spacing={4}>
                                                <CreateClientConfirmationStep />
                                                <Stack direction="row" spacing={2}>
                                                    <IndieBackButton id="back_button" variant="outlined" onClick={handleBack} disabled={submitting} />
                                                    <IndieSaveButton id="save_client_button" type="submit" variant="contained" loading={submitting} disabled={submitting} />
                                                </Stack>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                </Stepper>
                            </Form>
                        )}
                    </Formik>
                </TariffsContextProvider>
            </DialogContent>
        </Dialog>
    );
};

const CreateClientConfirmationStep = () => {

    const { values } = useFormikContext();
    const { t } = useTranslation();

    const clientTypeValue = (type) => values.client_type.includes(type) ? t("label.button.yes") : t("label.button.no");

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'>
                    {t("addClient.clientType.confirmationStep.subtitle")}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("addClient.clientType")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.tariffPlanType.consumer" value={clientTypeValue("consumer")} />
                    <IndieLabelAndValue label="label.tariffPlanType.producer" value={clientTypeValue("producer")} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.title.clientData")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.fiscalNumber" value={values.fiscal_number} />
                    <IndieLabelAndValue label="label.form.name" value={values.name} />
                    <IndieLabelAndValue label="label.form.email" value={values.email} />
                    <IndieLabelAndValue label="label.form.phone" value={values.phone} />
                    <IndieLabelAndValue label="label.form.zipCode" value={values.zip_code} />
                </Stack>
                {values.client_type.includes("producer") && (
                    <Stack spacing={1}>
                        <Typography variant='subtitle2' color='textSecondary'>{t("label.title.receivingInformation")}</Typography>
                        <Divider />
                        <IndieLabelAndValue label="label.form.paymentIBAN" value={values.iban} />
                        <IndieLabelAndValue label="label.form.taxRegime" value={values.receipt_tax_regime_object?.label} />
                        <IndieLabelAndValue label="label.form.hasOpenActivity" value={values.receipt_has_open_activity ? t("label.form.yes") : t("label.form.no")} />
                        <IndieLabelAndValue label="addClient.clientType.confirmationStep.autoInvoiceRegime" value={values.receipt_use_auto_invoicing ? t("label.form.yes") : t("label.form.no")} />
                    </Stack>
                )}
                {values.client_type.includes("consumer") && (
                    <Stack spacing={1}>
                        <Typography variant='subtitle2' color='textSecondary'>{t("label.title.paymentsInformation")}</Typography>
                        <Divider />
                        <IndieLabelAndValue label="label.form.paymentMethod" value={values.consumer_payment_method ? t(`invoicesTab.method.${values.consumer_payment_method_object?.code}`) : ""} />
                        {values.consumer_payment_method_object?.code === "sdd" && (
                            <>
                                <IndieLabelAndValue label="label.form.ddIBAN" value={values.consumer_dd_iban} />
                                <IndieLabelAndValue label="label.form.ddMandateReference" value={values.consumer_dd_mandate_reference} />
                                <IndieLabelAndValue label="label.form.ddMaxValue" value={values.consumer_dd_max_value} />
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
        </Fragment>
    )
}

export default CreateClientDialog;